/**
 * Prop/Bet Utilities
 * Parsing and formatting helpers for player props and team bets
 */

import type { LeagueKey } from '@parlay-picker/shared/league';
import type { GameMarket } from '@parlay-picker/shared/marketKey';
import {
  GAME_MARKETS,
  isGameTotalMarket,
  isTeamSideMarket,
  marketFamily,
} from '@parlay-picker/shared/marketKey';
import { normalizePlayerNameForDisplay } from '@parlay-picker/shared/playerNameUtils';
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import { canonicalizeStatType, marketKeyToStatType } from '@parlay-picker/shared/statType';

export { GAME_MARKETS };
export type { GameMarket };

// =============================================================================
// Types
// =============================================================================

export interface ParsedLeg {
  playerName: string;
  teamName?: string; // For team bets (h2h, spreads, totals)
  overUnder: string;
  line: number;
  propType: string;
  raw: string;
  isTeamBet?: boolean; // True for h2h, spreads, totals
}

/**
 * Game data from Odds API
 */
export interface Game {
  id: string;
  sport_key: LeagueKey;
  sport_title?: string;
  away_team?: string;
  home_team?: string;
  commence_time?: string;
  [key: string]: unknown;
}

/**
 * Bettable prop from Odds API
 */
export interface BettableProp {
  id: string;
  playerName?: string;
  team?: string;
  propType?: string;
  overUnder?: string;
  line?: number;
  odds?: string;
  gameId?: string;
  gameDate?: string; // ISO date string from game's commence_time
  [key: string]: unknown;
}

// =============================================================================
// Functions
// =============================================================================

/**
 * Parse input text into structured legs
 */
export function parseInputText(text: string): ParsedLeg[] {
  const lines = text.split('\n').filter((l) => l.trim());
  const parsed: ParsedLeg[] = [];

  for (const line of lines) {
    const leg = parseLegLine(line);
    if (leg) {
      parsed.push(leg);
    }
  }

  return parsed;
}

/**
 * Parse a single leg line
 * Supports both player props and team bets (spreads, moneyline, totals)
 */
export function parseLegLine(line: string): ParsedLeg | null {
  // Try team bet patterns first
  const teamBet = parseTeamBetLine(line);
  if (teamBet) return teamBet;

  // Patterns to match various player prop formats:
  // "Jayson Tatum Over 27.5 points"
  // "Tatum O27.5 pts"
  // "LeBron James points Over 25.5"
  const patterns = [
    // Full format: Player Over/Under Line Stat
    /^(.+?)\s+(over|under|o|u)\s*(\d+\.?\d*)\s*(points?|pts?|rebounds?|rebs?|rbs|reb|assists?|asts?|ast|threes?|3pm?|3s|blocks?|blks?|blk|steals?|stls?|stl|turnovers?|tos?|pts\+reb\+ast|pra|pts\+reb|pr|pts\+ast|pa|reb\+ast|ra)(?![\w+])/i,
    // Alternate: Player Stat Over/Under Line
    /^(.+?)\s+(points?|pts?|rebounds?|rebs?|rbs|reb|assists?|asts?|ast|threes?|3pm?|3s|blocks?|blks?|blk|steals?|stls?|stl|turnovers?|tos?|pts\+reb\+ast|pra|pts\+reb|pr|pts\+ast|pa|reb\+ast|ra)(?![\w+])\s+(over|under|o|u)\s*(\d+\.?\d*)/i,
  ];

  for (let i = 0; i < patterns.length; i++) {
    const match = line.match(patterns[i]);
    if (match) {
      if (i === 0) {
        return {
          playerName: normalizePlayerNameForDisplay(match[1]),
          overUnder: normalizeOverUnder(match[2]),
          line: parseFloat(match[3]),
          propType: canonicalizeStatType(match[4]),
          raw: line,
        };
      } else {
        return {
          playerName: normalizePlayerNameForDisplay(match[1]),
          propType: canonicalizeStatType(match[2]),
          overUnder: normalizeOverUnder(match[3]),
          line: parseFloat(match[4]),
          raw: line,
        };
      }
    }
  }

  return null;
}

/**
 * Parse team bet lines (spreads, moneyline, game totals)
 * Formats supported:
 * - "Lakers -5.5" or "Lakers +3.5" (spread)
 * - "Lakers ML" or "Lakers moneyline" (moneyline)
 * - "Lakers vs Celtics Over 220.5" or "Game Total Over 220.5" (totals)
 */
function parseTeamBetLine(line: string): ParsedLeg | null {
  const trimmed = line.trim();

  // Spread: "Lakers -5.5" or "Celtics +3.5"
  const spreadMatch = trimmed.match(/^(.+?)\s+([+-]\d+\.?\d*)$/i);
  if (spreadMatch) {
    const teamName = spreadMatch[1].trim();
    const spreadValue = parseFloat(spreadMatch[2]);
    return {
      playerName: teamName, // Use playerName field for team name (backward compatible)
      teamName: teamName,
      overUnder: spreadValue < 0 ? 'favorite' : 'underdog',
      line: spreadValue,
      propType: 'spreads',
      raw: line,
      isTeamBet: true,
    };
  }

  // Moneyline: "Lakers ML" or "Lakers moneyline" or "Lakers to win"
  const mlMatch = trimmed.match(/^(.+?)\s+(ml|moneyline|to\s+win)$/i);
  if (mlMatch) {
    const teamName = mlMatch[1].trim();
    return {
      playerName: teamName,
      teamName: teamName,
      overUnder: 'win',
      line: 0, // No line for moneyline
      propType: 'h2h',
      raw: line,
      isTeamBet: true,
    };
  }

  // Game Total: "Over 220.5" or "Under 215.5" or "Lakers vs Celtics O220.5"
  // Also matches: "Game Total Over 220.5"
  // IMPORTANT: Only match if the line is game-total range (typically 180-260 for NBA)
  // or if explicitly prefixed with "game" or "total" or "vs"
  // This prevents matching "Curry points Over 24.5" as a game total
  const totalMatch = trimmed.match(
    /^(?:(.+?)\s+)?(?:game\s+)?(?:total\s+)?(over|under|o|u)\s*(\d+\.?\d*)$/i,
  );
  if (totalMatch) {
    const teams = totalMatch[1]?.trim() || '';
    const overUnder = normalizeOverUnder(totalMatch[2]);
    const total = parseFloat(totalMatch[3]);

    // Only treat as game total if:
    // 1. Line is >= 100 (clearly a game total, not player prop)
    // 2. OR explicitly has "game", "total", or "vs" in prefix
    // 3. OR no prefix at all (just "Over 220.5")
    const isLikelyGameTotal = total >= 100 || !teams || /\bvs\b|\bgame\b|\btotal\b/i.test(teams);

    if (isLikelyGameTotal) {
      return {
        playerName: teams || 'Game',
        teamName: teams || 'Game',
        overUnder: overUnder,
        line: total,
        propType: 'totals',
        raw: line,
        isTeamBet: true,
      };
    }
  }

  return null;
}

// Removed - now using shared normalizePlayerNameForDisplay (imported above)

export function normalizeOverUnder(value: string): string {
  const lower = value.toLowerCase();
  if (lower === 'o' || lower === 'over') return 'Over';
  if (lower === 'u' || lower === 'under') return 'Under';
  return lower;
}

export function formatPropType(propType: string | undefined): string {
  if (!propType) return '';

  // Detect period suffix (e.g., _h1, _h2, _q1-_q4)
  const periodMatch = propType.match(/^(.+)_(h[12]|q[1-4])$/);
  const periodSuffix = periodMatch ? periodMatch[2] : null;
  const withoutPeriod = periodMatch ? periodMatch[1] : propType;

  const periodLabels: Record<string, string> = {
    h1: '1H',
    h2: '2H',
    q1: 'Q1',
    q2: 'Q2',
    q3: 'Q3',
    q4: 'Q4',
  };

  const isAlt = withoutPeriod.endsWith('_alternate');
  let base = isAlt ? withoutPeriod.slice(0, -10) : withoutPeriod; // Remove '_alternate' (10 chars)

  // Strip 'player_' prefix if present (e.g., "player_assists" -> "assists")
  if (base.startsWith('player_')) {
    base = base.slice(7);
  }

  const formatted = formatStatLabel(base);
  let result = isAlt ? `${formatted} Alt` : formatted;
  if (periodSuffix && periodLabels[periodSuffix]) {
    result = `${periodLabels[periodSuffix]} ${result}`;
  }
  return result;
}

/**
 * Check if a search term matches a prop type (including abbreviations)
 * e.g., "pt" matches "points", "reb" matches "rebounds"
 */
export function matchesPropType(propType: string | undefined, search: string | undefined): boolean {
  if (!propType || !search) return false;
  const lower = propType.toLowerCase();
  const searchLower = search.toLowerCase();

  // Direct match on prop type
  if (lower.includes(searchLower)) return true;

  // Check if search matches any abbreviation for this prop type
  const abbreviationMap: Record<string, string[]> = {
    // Basic stats
    points: ['pts', 'pt', 'point'],
    rebounds: ['reb', 'rebs', 'rebound'],
    assists: ['ast', 'asts', 'assist'],
    threes: ['3pm', '3s', '3', 'three'],
    blocks: ['blk', 'blks', 'block'],
    steals: ['stl', 'stls', 'steal'],
    turnovers: ['to', 'turnover'],
    // Shooting props
    field_goals: ['fgm', 'fg', 'field goal'],
    frees_made: ['ftm', 'ft', 'free throw'],
    // Combo props
    blocks_steals: ['blk+stl', 'bs'],
    'pts+reb+ast': ['pra'],
    'pts+reb': ['pr'],
    'pts+ast': ['pa'],
    'reb+ast': ['ra'],
    // Special props
    double_double: ['dd', 'double'],
    triple_double: ['td', 'triple'],
    first_basket: ['first', 'first scorer'],
    first_team_basket: ['first team'],
    // Game-level markets
    h2h: ['ml', 'moneyline', 'money', 'win'],
    spreads: ['spread', 'line', 'ats'],
    totals: ['total', 'ou', 'over/under', 'game total'],
    // Team/period categories
    team_totals: ['team total', 'team', 'tt'],
    half: ['1h', '2h', 'half', 'first half', 'second half', '1st half', '2nd half'],
    quarter: ['q1', 'q2', 'q3', 'q4', 'quarter', 'first quarter'],
  };

  const abbrevs = abbreviationMap[lower] || [];
  // Match if search equals abbreviation, or partial match in either direction
  if (
    abbrevs.some(
      (abbr) =>
        abbr === searchLower || abbr.startsWith(searchLower) || searchLower.startsWith(abbr),
    )
  )
    return true;

  // Category-level matching for filter keys
  if (searchLower === 'team_totals' && lower.includes('team_total')) return true;
  if (searchLower === 'half' && /_h[12]$/.test(lower)) return true;
  if (searchLower === 'quarter' && /_q[1-4]$/.test(lower)) return true;

  return false;
}

/**
 * Get verdict color classes
 */
export function getVerdictColor(verdict: string | undefined): string {
  switch (verdict?.toLowerCase()) {
    case 'good':
      return 'text-theme-success bg-theme-success/10 border-theme-success';
    case 'okay':
      return 'text-theme-warning bg-theme-warning/10 border-theme-warning';
    case 'risky':
      return 'text-orange-400 bg-orange-400/10 border-orange-400';
    case 'bad':
      return 'text-theme-danger bg-theme-danger/10 border-theme-danger';
    default:
      return 'text-theme-text-secondary bg-theme-surface border-theme-border';
  }
}

export function getVerdictIcon(verdict: string | undefined): string {
  switch (verdict?.toLowerCase()) {
    case 'good':
      return '✅';
    case 'okay':
      return '⚠️';
    case 'risky':
      return '⚠️';
    case 'bad':
      return '❌';
    default:
      return '❓';
  }
}

/** A prop's StatKey: an Odds API market key through the market table, any other spelling canonicalized. */
export function propTypeToStatType(propType: string | undefined): string {
  if (!propType) return '';
  return marketKeyToStatType(propType) ?? canonicalizeStatType(propType);
}

// =============================================================================
// Display Helpers for Team Bets
// =============================================================================

/**
 * Common interface for prop/leg data that can be displayed
 */
export interface DisplayableProp {
  playerName?: string;
  team?: string;
  propType?: string;
  overUnder?: string;
  line?: number;
  gameId?: string;
}

/**
 * Common interface for game data
 */
export interface GameInfo {
  id: string;
  home_team?: string;
  away_team?: string;
}

/**
 * Get display name for a prop/leg based on its type
 * - Totals, moneylines and spreads of any period: show game matchup (e.g., "Hawks @ Celtics")
 * - Player props and team totals: show player or team name
 */
export function getBetDisplayName(prop: DisplayableProp, games?: GameInfo[]): string {
  const propType = prop.propType?.toLowerCase();

  if (isTeamSideMarket(propType) || isGameTotalMarket(propType)) {
    // For all team bets, show the game matchup
    if (games && prop.gameId) {
      const game = games.find((g) => g.id === prop.gameId);
      if (game) {
        const awayShort = game.away_team?.split(' ').pop() || game.away_team;
        const homeShort = game.home_team?.split(' ').pop() || game.home_team;
        return `${awayShort} @ ${homeShort}`;
      }
    }
    // Fallback
    if (isGameTotalMarket(propType)) {
      const context = prop.playerName;
      return context && context !== 'Game' ? `${context}` : 'Game Total';
    }
    return prop.team || prop.playerName || 'Team';
  }
  // Player props - show player name
  return prop.playerName || prop.team || 'Player';
}

/**
 * Get display prop string based on bet type
 * - Totals: "Over 220.5"
 * - Moneyline: "Hawks Moneyline" (team in prop since name shows matchup)
 * - Spreads: "Celtics -5.5" (team + line since name shows matchup)
 * - Team totals: "Lakers Over 112.5"
 * - A period or alternate game market adds its market name: "Celtics -2.5 1H Spread"
 * - Player props: "Over 25.5 Points"
 */
export function getBetDisplayProp(prop: DisplayableProp): string {
  const propType = prop.propType?.toLowerCase();
  const line = prop.line;
  const overUnder = prop.overUnder;
  const teamName = prop.team || prop.playerName;
  const family = propType ? marketFamily(propType) : undefined;
  const marketName = propType ? formatPropType(propType) : '';
  const qualifier = propType && propType !== family ? ` ${marketName}` : '';

  if (family === 'totals') {
    // Just "Over 220.5" without redundant "Total"
    return `${overUnder} ${line}${qualifier}`;
  }
  if (family === 'h2h') {
    // Show team name with Moneyline, e.g., "Lakers Moneyline"
    return teamName ? `${teamName} ${marketName}` : marketName;
  }
  if (family === 'spreads') {
    // Show team name with spread, e.g., "Celtics -5.5"
    const spreadLine = typeof line === 'number' ? (line >= 0 ? `+${line}` : `${line}`) : line;
    return teamName ? `${teamName} ${spreadLine}${qualifier}` : `${marketName} ${spreadLine}`;
  }
  if (family === 'team_totals') {
    // Show team name with total, e.g., "Lakers Over 112.5"
    return teamName
      ? `${teamName} ${overUnder} ${line}${qualifier}`
      : `${overUnder} ${line} ${marketName}`;
  }
  // Player props - standard format
  // Handle special props without over/under or line (double-double, first basket, etc.)
  if (overUnder == null && line == null) {
    return formatPropType(propType);
  }
  return `${overUnder ?? ''} ${line ?? ''} ${formatPropType(propType)}`.trim();
}
